import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useEvent } from "../context/EventProvider";
import { useSettings } from "../settings/context";
import {
  atRest,
  composing,
  hasFailedDraft,
  saveDrafts,
  usePendingDrafts,
} from "./formDraft";

/**
 * OÙ EN EST L'ENVOI DES FORMULAIRES — le pendant de `SyncState` pour les réglages.
 *
 * ⚠️ SANS LUI, ENREGISTRER UN FORMULAIRE NE DISAIT RIEN (relevé par William le
 * 2026-09-13 : « on voit ça puis plus rien »). Le badge ne lisait que l'état des
 * réglages : quand seul un formulaire attendait, l'envoi partait sans « Enregistrement »,
 * puis le compte des brouillons tombait à zéro, l'état des réglages était toujours
 * `idle`, et le badge disparaissait. Ni « Enregistré », ni « Échec ». Pas de
 * « waiting » ici : l'attente, c'est `pending > 0`, que la barre lit déjà.
 */
export type FormSaveState = "idle" | "saving" | "saved" | "failed";

/**
 * LE FORMULAIRE S'ENREGISTRE COMME LES RÉGLAGES — automatique ou manuel, au choix de
 * l'organisateur.
 *
 * ⚠️ CE CHOIX N'APPARTIENT PAS À UN ÉCRAN. L'éditeur avait son propre bouton
 * « Enregistrer le formulaire », qui envoyait quel que soit le mode : en automatique
 * il fallait quand même cliquer, en manuel il y avait deux boutons pour une seule
 * intention. Le mode dit comment CETTE personne travaille, et tout ce qu'elle modifie
 * doit le suivre.
 *
 * ⚠️ MONTÉ DANS LA BARRE, PAS DANS L'ÉDITEUR. L'envoi doit pouvoir partir quand
 * l'éditeur est démonté — la carte du `SpotEditor` refermée, un autre écran affiché —
 * sans quoi l'automatique ne serait automatique que tant qu'on regarde. La barre, elle,
 * est là dès qu'on est en mode organisateur.
 *
 * ⚠️ MÊME CADENCE QUE LES RÉGLAGES, à dessein. Écrire à chaque frappe serait pire ici
 * qu'ailleurs : la route détruit le formulaire et le réinsère, donc chaque envoi est
 * une réécriture complète et une occasion d'écraser le travail d'un collègue. Six
 * secondes de silence, et une seule écriture.
 */

/** Le même délai que `useAutoSave`. Deux rythmes différents se remarqueraient. */
const EVERY = 6_000;

export function useFormSaving() {
  const event = useEvent();
  const { saveMode, saving } = useSettings();
  const client = useQueryClient();
  const pending = usePendingDrafts();

  const afterEach = useCallback(async () => {
    /* Le formulaire vient d'être réécrit : les identifiants de placement ont changé,
       et l'écran informations lit le même cache. */
    await client.invalidateQueries({
      queryKey: ["forms", event?.domainId, event?.eventId],
    });
  }, [client, event]);

  /* Même verrou que `useAutoSave` : deux envois qui se chevauchent sont deux
     « détruit puis réinsère » concurrents sur le même formulaire. */
  const inFlight = useRef(false);
  const [state, setState] = useState<FormSaveState>("idle");
  /* Le bouton manuel appelle `send` même sans brouillon : rien à envoyer, rien à dire. */
  const pendingRef = useRef(pending);
  pendingRef.current = pending;
  const send = useCallback(async () => {
    if (inFlight.current || pendingRef.current === 0) return;
    inFlight.current = true;
    setState("saving");
    try {
      await saveDrafts(afterEach);
      /* `saveDrafts` ne lève jamais : un échec est rangé SUR le brouillon, qui reste
         en attente. C'est là qu'on le lit. */
      setState(hasFailedDraft() ? "failed" : "saved");
    } finally {
      inFlight.current = false;
    }
  }, [afterEach]);

  /**
   * ⚠️ EN MANUEL, RIEN NE PART TOUT SEUL — ni sur minuterie, ni en quittant, ni au
   * démontage. C'est la règle que `useAutoSave` s'impose déjà, mot pour mot : quelqu'un
   * qui a demandé à décider du moment ne s'attend pas à ce que ça parte quand même dès
   * qu'il regarde ailleurs.
   */
  useEffect(() => {
    if (!saving || saveMode !== "auto" || pending === 0) return;
    /* ⚠️ LE TOUR SE SAUTE, LA MINUTERIE NE S'ARRÊTE PAS. Une retouche de plus repousse
       l'envoi au tour suivant — voir `atRest`, qui dit pourquoi un envoi lancé au milieu
       d'une retouche laisse le brouillon périmé. Un échec, lui, continue d'être
       réessayé : il ne touche à rien, donc le brouillon est au repos dès le tour
       suivant. */
    const timer = window.setInterval(() => {
      if (atRest()) void send();
    }, EVERY);
    // Vidage au masquage, comme les réglages : `visibilitychange` est le seul événement
    // fiable sur mobile, où `beforeunload` est ignoré.
    const onHide = () => {
      /* ⚠️ MAIS PAS PENDANT UNE CRÉATION DE DONNÉE, et là seulement : partir avec la
         modale ouverte enverrait un brouillon que la pose du nouveau champ va
         ressusciter, périmé, quelques instants plus tard. Rien n'est perdu pour autant —
         le brouillon reste en mémoire et l'avertissement de sortie ci-dessous est armé.
         Ailleurs le vidage part sans attendre le repos : quitter, c'est maintenant. */
      if (document.visibilityState === "hidden" && !composing()) void send();
    };
    document.addEventListener("visibilitychange", onHide);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onHide);
    };
  }, [saving, saveMode, pending, send]);

  /**
   * ⚠️ ET L'AVERTISSEMENT DE SORTIE, que les brouillons n'avaient PAS. Le filet des
   * réglages (`useAutoSave`) ne regarde que LEUR état, pas les formulaires en attente :
   * dix largeurs de champ retouchées puis l'onglet fermé partaient sans un mot, en mode
   * manuel surtout. On l'arme dès qu'un brouillon attend, quel que soit le mode.
   */
  useEffect(() => {
    if (!saving || pending === 0) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [saving, pending]);

  return { pending, send, state };
}
